import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    FaArrowLeft, FaCheck, FaBook, FaRobot, FaPaperPlane, FaBars,
    FaChevronDown, FaChevronRight, FaPlus
} from 'react-icons/fa';
import './ModulePage.css';
import { db } from '../firebase'; // Importe sua configuração do Firebase
import { doc, updateDoc } from 'firebase/firestore';

import { addAction, removeAction, getUserModule } from "../services/modules";
import FocusLoader from '../components/FocusLoader';
import ResourceAccessPrompt from '../components/ResourceAccessPrompt';


const ModulePage = () => {
    const { moduleId } = useParams();
    const [module, setModule] = useState(null);

    const navigate = useNavigate();
    const [activeTopic, setActiveTopic] = useState(null);
    const [activeTask, setActiveTask] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showSidebar, setShowSidebar] = useState(false);
    const [expandedTopics, setExpandedTopics] = useState({});
    const messagesEndRef = useRef(null);

    // Carregar dados do módulo do Firestore
    useEffect(() => {
        setIsLoading(true);
        getUserModule(moduleId).then(moduleData => {
            if (moduleData) {
                setModule(moduleData);
                // Expandir o primeiro tópico por padrão
                if (moduleData?.topicos && moduleData?.topicos?.length > 0) {
                    setExpandedTopics({ [moduleData?.topicos[0]?.id]: true });
                    setActiveTopic(moduleData?.topicos[0]);

                    // Verificar se há tarefas criadas
                    if (moduleData?.topicos[0]?.tarefas && moduleData?.topicos[0]?.tarefas.length > 0) {
                        setActiveTask(moduleData?.topicos[0]?.tarefas[0]);
                    }
                }
                setIsLoading(false);
            } else {
                setIsLoading(false);
                // criar com estrutura básica
            }
        });

    }, [moduleId]);

    // Salvar módulo no Firestore
    const saveModuleToFirestore = async (updatedModule) => {
        try {
            const docRef = doc(db, "modules", moduleId);
            await updateDoc(docRef, updatedModule);
        } catch (error) {
            console.error("Erro ao salvar módulo:", error);
        }
    };

    // Iniciar conversa com o assistente
    useEffect(() => {
        if (module && activeTopic && activeTask) {
            setMessages([
                {
                    id: 1,
                    text: `Vamos trabalhar na tarefa: "${activeTask?.titulo}". Como posso te ajudar?`,
                    sender: 'assistant'
                }
            ]);
        } else if (module) {
            setMessages([
                {
                    id: 1,
                    text: `Olá! Vamos começar o módulo ${module?.titulo}. Responda algumas perguntas para eu criar seu plano personalizado.`,
                    sender: 'assistant'
                },
                {
                    id: 2,
                    text: "Qual é sua área de maior interesse profissional?",
                    sender: 'assistant'
                }
            ]);
        }
    }, [module, activeTopic, activeTask]);

    // Rolar para a última mensagem automaticamente
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Enviar mensagem para o assistente
    const sendMessage = () => {
        if (inputMessage.trim() === '') return;

        // Adicionar mensagem do usuário
        const userMessage = {
            id: messages.length + 1,
            text: inputMessage,
            sender: 'user'
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');

        // Simular envio para a API Gemini
        // Em produção, seria uma chamada real à API com sessionId e chatId
        setTimeout(() => {
            // Verificar se precisamos criar tarefas/acoes
            if (!activeTopic || !activeTask) {
                // Criar tópico e tarefas com base na resposta do usuário
                createTasksFromUserInput(inputMessage);
            } else {
                // Resposta genérica para outras mensagens
                const assistantMessage = {
                    id: messages.length + 2,
                    text: `Entendi: "${inputMessage}". Vamos continuar trabalhando na tarefa atual.`,
                    sender: 'assistant'
                };

                setMessages(prev => [...prev, assistantMessage]);
            }
        }, 1000);
    };

    // Criar tarefas a partir da entrada do usuário (simulação)
    const createTasksFromUserInput = (userInput) => {
        // Simulação de criação de tópico e tarefas
        const newTopic = {
            id: Date.now(),
            titulo: "Plano de Desenvolvimento",
            descricao: "Tópico criado com base nas suas respostas",
            tarefas: [
                {
                    id: Date.now() + 1,
                    titulo: "Explorar áreas de interesse",
                    acoes: [
                        {
                            id: Date.now() + 2,
                            titulo: "Pesquisar carreiras relacionadas",
                            descricao: "Buscar informações sobre 3 profissões que combinam com seus interesses",
                            concluido: false
                        },
                        {
                            id: Date.now() + 3,
                            titulo: "Entrevistar profissionais",
                            descricao: "Conversar com alguém que trabalha na área que você tem interesse",
                            concluido: false
                        }
                    ]
                },
                {
                    id: Date.now() + 4,
                    titulo: "Desenvolver habilidades essenciais",
                    acoes: [
                        {
                            id: Date.now() + 5,
                            titulo: "Identificar habilidades necessárias",
                            descricao: "Listar 5 habilidades importantes para sua área de interesse",
                            concluido: false
                        }
                    ]
                }
            ]
        };

        const updatedModule = {
            ...module,
            topicos: [...(module?.topicos || []), newTopic]
        };

        setModule(updatedModule);
        setActiveTopic(newTopic);
        setActiveTask(newTopic.tarefas[0]);
        setExpandedTopics(prev => ({ ...prev, [newTopic.id]: true }));

        // Salvar no Firestore
        saveModuleToFirestore(updatedModule);

        // Nova mensagem do assistente
        const assistantMessage = {
            id: messages.length + 2,
            text: "Ótimo! Criei seu plano personalizado com tarefas e ações. Vamos começar!",
            sender: 'assistant'
        };

        setMessages(prev => [...prev, assistantMessage]);
    };

    // Alternar expansão de tópico
    const toggleTopicExpansion = (topicId) => {
        setExpandedTopics(prev => ({
            ...prev,
            [topicId]: !prev[topicId]
        }));
    };

    // Marcar ação como concluída
    const toggleActionCompletion = (actionId) => {
        const updatedModule = { ...module };

        // Encontrar e atualizar a ação
        updatedModule?.topicos?.forEach(topic => {
            topic.tarefas.forEach(task => {
                task.acoes.forEach(action => {
                    if (action.id === actionId) {
                        action.concluido = !action.concluido;
                    }
                });
            });
        });

        setModule(updatedModule);
        saveModuleToFirestore(updatedModule);
    };

    // Adicionar nova ação a uma tarefa
    const addNewAction = (taskId) => {
        const newAction = {
            id: Date.now(),
            titulo: "Nova ação",
            descricao: "Descrição da nova ação",
            concluido: false
        };

        const updatedModule = { ...module };

        updatedModule?.topicos?.forEach(topic => {
            topic.tarefas.forEach(task => {
                if (task.id === taskId) {
                    task.acoes.push(newAction);
                }
            });
        });

        setModule(updatedModule);
        saveModuleToFirestore(updatedModule);
    };


    const onAddAction = () => {
        const newAction = {
            id: Date.now(),
            titulo: "Nova Ação",
            descricao: "Descrição…",
            concluido: false
        };
        // adiciona na topico 0, tarefa 0
        addAction(moduleId, 0, 0, newAction).then(() => {
            setModule(m => {
                const m2 = { ...m };
                m2.topicos[0].tarefas[0].acoes.push(newAction);
                return m2;
            });
        });
    };

    const onRemoveAction = action => {
        removeAction(moduleId, 0, 0, action).then(() => {
            setModule(m => {
                const m2 = { ...m };
                m2.topicos[0].tarefas[0].acoes =
                    m2.topicos[0].tarefas[0].acoes.filter(a => a.id !== action.id);
                return m2;
            });
        });
    };
    const trial = false;
    
    if (trial) {
        onRemoveAction();
        onAddAction();
    }

    if (!module) {
        <ResourceAccessPrompt />
    } else {
        console.log("------------------------------------------")
        console.log(module)
        console.log("------------------------------------------")
    }
    if (isLoading) return <FocusLoader />;


    return (
        <div className="module-details-container">
            {/* Cabeçalho Mobile */}
            <div className="mobile-header">
                <button
                    className="menu-toggle"
                    onClick={() => setShowSidebar(!showSidebar)}
                >
                    <FaBars />
                </button>
                <div className="module-title">
                    <h1>{module?.titulo}</h1>
                    {activeTask && <p>{activeTask.titulo}</p>}
                </div>
                <button
                    className="back-button"
                    onClick={() => navigate('/dashboard')}
                >
                    <FaArrowLeft />
                </button>
            </div>

            {/* Conteúdo Principal */}
            <div className="module-content">
                {/* Menu Lateral - Visível em desktop, toggle em mobile */}
                <div className={`module-sidebar ${showSidebar ? 'active' : ''}`}>
                    <div className="sidebar-header">
                        <h2>{module?.titulo}</h2>
                        <button
                            className="close-sidebar"
                            onClick={() => setShowSidebar(false)}
                        >
                            &times;
                        </button>
                    </div>

                    <div className="topics-list">
                        {module?.topicos?.map(topic => (
                            <div
                                key={topic?.id}
                                className="topic-item"
                            >
                                <div
                                    className="topic-header"
                                    onClick={() => toggleTopicExpansion(topic.id)}
                                >
                                    <div className="topic-info">
                                        <FaBook className="topic-icon" />
                                        <h3>{topic?.titulo}</h3>
                                    </div>
                                    <button className="expand-toggle">
                                        {expandedTopics[topic.id] ? <FaChevronDown /> : <FaChevronRight />}
                                    </button>
                                </div>

                                {expandedTopics[topic.id] && (
                                    <div className="tasks-list">
                                        {topic.tarefas.map(task => (
                                            <div
                                                key={task.id}
                                                className={`task-item ${activeTask?.id === task.id ? 'active' : ''}`}
                                                onClick={() => {
                                                    setActiveTopic(topic);
                                                    setActiveTask(task);
                                                    setShowSidebar(false);
                                                }}
                                            >
                                                <div className="task-info">
                                                    <FaCheck className="task-icon" />
                                                    <h4>{task?.titulo}</h4>
                                                </div>

                                                <div className="actions-preview">
                                                    {task.acoes.slice(0, 2).map((action, index) => (
                                                        <div
                                                            key={index}
                                                            className={`action-preview ${action.concluido ? 'completed' : ''}`}
                                                        >
                                                            <span>{action?.titulo}</span>
                                                        </div>
                                                    ))}
                                                    {task.acoes.length > 2 && (
                                                        <div className="more-actions">
                                                            +{task.acoes.length - 2} ações
                                                        </div>
                                                    )}
                                                </div>

                                                <button
                                                    className="add-action-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        addNewAction(task.id);
                                                    }}
                                                >
                                                    <FaPlus />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}

                        {(!module?.topicos || module?.topicos?.length === 0) && (
                            <div className="no-topics">
                                <p>Nenhum tópico criado ainda. Converse com o assistente para começar!</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Área de Chat */}
                <div className="module-chat">
                    <div className="chat-header">
                        <div className="assistant-info">
                            <FaRobot className="assistant-icon" />
                            <h3>Assistente de IA</h3>
                        </div>
                        {activeTask && (
                            <div className="current-task">
                                <span>{activeTask?.titulo}</span>
                            </div>
                        )}
                    </div>

                    <div className="chat-messages">
                        {messages.map(message => (
                            <div
                                key={message.id}
                                className={`message ${message.sender}`}
                            >
                                {message.text}
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="chat-input-container">
                        <textarea
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            placeholder="Digite sua mensagem para o assistente..."
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    sendMessage();
                                }
                            }}
                        />
                        <button
                            className="send-button"
                            onClick={sendMessage}
                        >
                            <FaPaperPlane />
                        </button>
                    </div>
                </div>

                {/* Painel de Ações (Oculto em mobile) */}
                {activeTask && (
                    <div className="actions-panel">
                        <div className="panel-header">
                            <h3>Ações da Tarefa</h3>
                        </div>

                        <div className="actions-list">
                            {activeTask.acoes.map(action => (
                                <div
                                    key={action.id}
                                    className={`action-item ${action.concluido ? 'completed' : ''}`}
                                >
                                    <div
                                        className="action-checkbox"
                                        onClick={() => toggleActionCompletion(action.id)}
                                    >
                                        {action.concluido ? <FaCheck /> : <div className="empty-checkbox" />}
                                    </div>
                                    <div className="action-content">
                                        <h4>{action?.titulo}</h4>
                                        <p>{action.descricao}</p>
                                    </div>
                                </div>
                            ))}

                            {activeTask.acoes.length === 0 && (
                                <div className="no-actions">
                                    <p>Nenhuma ação criada ainda. Adicione ações ou converse com o assistente!</p>
                                </div>
                            )}
                        </div>

                        <button
                            className="add-action-btn"
                            onClick={() => addNewAction(activeTask.id)}
                        >
                            <FaPlus /> Adicionar ação
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ModulePage;